import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { api, INSTAGRAM_URL, LOGO } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Stagger, StaggerItem } from '../components/Reveal';
import { Brush } from '../components/Brush';
import { Img } from '../components/Img';
import { LoopVideo } from '../components/LoopVideo';
import { CountUp } from '../components/CountUp';
import { ScrubWords } from '../components/ScrubWords';
import { MateriaChapters } from '../components/company/MateriaChapters';
import { Miniserie, puntateJsonLd } from '../components/company/Miniserie';
import { useCollections } from '../lib/useCatalog';
import { collectionCover, REAL_COVER_SLUGS } from '../lib/collectionCovers';

const C = '/assets/company';
const loop = (nome) => ({
  src: `${C}/${nome}.mp4`,
  srcMobile: `${C}/${nome}-720.mp4`,
  poster: `${C}/${nome}-poster.jpg`,
  width: 1920,
  height: 1080,
});

/** Loop di materia generati con AI (A-15). */
const CAPITOLI = [
  { key: 'origin', video: loop('travertino') },
  { key: 'studio', video: loop('smalto') },
  { key: 'territory', video: loop('acqua') },
];

/** Ripresa reale dello showroom (taglio da 8 s), approvata al checkpoint Task 3–4. */
const SHOWROOM = loop('showroom');

export default function Company() {
  const { t, lang } = useLang();
  const [c, setC] = useState(null);
  const { data: collezioni } = useCollections(lang);
  const nomi = Object.fromEntries((collezioni?.items || []).map((x) => [x.slug, x.name]));

  useEffect(() => {
    api.get(`/company?lang=${lang}`).then((r) => setC(r.data));
  }, [lang]);

  // [valore, etichetta, conta?] — 1996 e le stime tonde non contano
  const numbers = c
    ? [
        ['1996', lang === 'en' ? 'Founded in Tivoli' : 'Fondata a Tivoli', false],
        [c.collections, lang === 'en' ? 'Collections' : 'Collezioni', true],
        [c.references, lang === 'en' ? 'References' : 'Referenze', true],
        [c.stores, lang === 'en' ? 'Satur Passione Casa stores' : 'Negozi Satur Passione Casa', true],
      ]
    : [];

  // Barbara Vitale (feedback 02/11/2026): la frase apre il post Instagram del
  // 25/09/2026 che lancia la miniserie dei 30 anni (attribuzione confermata
  // dall'azienda il 02/11/2026). Prima c'era una voce
  // interna, e prima ancora Frida Kahlo (tolta: citazione senza fonte).
  const citazione =
    lang === 'en'
      ? '«I wanted to create a one-of-a-kind collection, designed by a woman for women.»'
      : '«Volevo creare una collezione unica, ideata da una donna per le donne.»';
  const citazioneFonte = 'Barbara Vitale, brand director VdE Tivoli 1996';

  return (
    <>
      <Seo
        title={t('company.title')}
        description={t('company.lead')}
        path="/azienda"
        image={collectionCover(REAL_COVER_SLUGS[0])}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: 'VdE Tivoli 1996',
              foundingDate: '1996',
              url: '/',
              logo: LOGO.png,
              description: t('company.lead'),
              sameAs: [INSTAGRAM_URL],
            },
            ...puntateJsonLd(t),
          ],
        }}
      />

      {/* ─── apertura: il nome enorme su carta, il travertino che respira dietro ─── */}
      <header data-testid="company-hero" className="relative overflow-hidden">
        <LoopVideo
          poster={CAPITOLI[0].video.poster}
          width={CAPITOLI[0].video.width}
          height={CAPITOLI[0].video.height}
          priority
          testid="company-hero-video"
          className="absolute inset-0"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-carta/80 md:bg-carta/70"
        />
        <div className="wrap relative flex min-h-[78svh] flex-col justify-end py-16 md:py-24">
          <h1 className="relative max-w-[14ch] text-[clamp(2.5rem,7vw,5.75rem)] leading-[0.95]">
            <span>{t('company.title')}</span>
            <Brush className="absolute -bottom-3 left-0 h-[0.3em] w-[54%]" opacity={0.32} />
          </h1>
          <p className="prosa mt-9 max-w-[52ch] border-t-2 border-inchiostro pt-6 text-lg">{t('company.lead')}</p>
        </div>
      </header>

      <MateriaChapters capitoli={CAPITOLI} />

      <section data-testid="company-numbers" className="py-16 md:py-24">
        <div className="wrap">
          <Stagger className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-4" gap={0.05}>
            {numbers.map(([v, l, conta]) => (
              <StaggerItem key={l}>
                <p className="font-display text-[clamp(3rem,7vw,5.5rem)] leading-none">
                  {conta ? <CountUp value={v} /> : v}
                </p>
                <p className="mt-3 text-[14px] font-semibold text-inchiostro/70">{l}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ─── dalla materia alla tavola: solo scatti reali ─── */}
      <section data-testid="company-table" className="py-16 md:py-24">
        <div className="wrap">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="text-[clamp(2rem,5vw,3.6rem)] leading-[1.04]">{t('company.tableTitle')}</h2>
              <p className="prosa mt-4 max-w-[46ch]">{t('company.tableText')}</p>
            </div>
            <Link to="/collezioni" className="btn btn-line">
              {t('nav.collections')}
              <ArrowUpRight size={15} strokeWidth={2} />
            </Link>
          </div>
          <ul className="fila fila-margini -mx-[var(--gutter)] mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-[var(--gutter)] pb-2 md:gap-6">
            {REAL_COVER_SLUGS.map((slug) => (
              <li key={slug} data-slug={slug} className="w-[62%] shrink-0 snap-start sm:w-[36%] lg:w-[22%]">
                <Link to={`/collezioni/${slug}`} className="group block">
                  <div className="aspect-[3/4] overflow-hidden bg-carta-2">
                    <Img
                      src={collectionCover(slug)}
                      alt={nomi[slug] || slug}
                      width={640}
                      aspectRatio={3 / 4}
                      sizes="(min-width: 1024px) 22vw, 62vw"
                      className="img-zoom h-full w-full object-cover"
                    />
                  </div>
                  <p className="mt-3 font-display text-xl">{nomi[slug] || slug}</p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {SHOWROOM && (
        <section data-testid="company-showroom" className="py-16 md:py-24">
          <div className="wrap">
            <h2 className="text-[clamp(2rem,5vw,3.6rem)] leading-[1.04]">{t('company.showroomTitle')}</h2>
            <LoopVideo {...SHOWROOM} label={t('company.showroomAlt')} className="relative mt-10 aspect-video bg-carta-2" />
          </div>
        </section>
      )}

      <section data-testid="company-values" className="bg-carta-2 py-16 md:py-24">
        <div className="wrap">
          <p className="accento mb-10 text-2xl text-inchiostro/85">{t('company.valuesTitle')}</p>
          <Stagger className="grid gap-px bg-inchiostro/12 md:grid-cols-3" gap={0.08}>
            {t('company.values').map(([title, text], i) => (
              <StaggerItem key={title} className="bg-carta-2 p-7 md:p-10">
                <h3 className="mt-6 font-display text-3xl">{title}</h3>
                <p className="prosa mt-4 text-sm">{text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section data-testid="company-quote" className="bg-notte pb-14 pt-20 text-carta md:pb-20 md:pt-32">
        <div className="wrap">
          <blockquote>
            <ScrubWords
              text={citazione}
              className="max-w-[26ch] font-display text-[clamp(2rem,5vw,4rem)] italic leading-[1.1]"
            />
            <cite className="mt-8 block text-[14px] font-bold not-italic text-carta/70">
              {citazioneFonte}
            </cite>
          </blockquote>
        </div>
      </section>

      {/* la frase sopra apre la prima puntata: la miniserie la continua */}
      <Miniserie />
    </>
  );
}
